import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Coins, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

export function ProjectCost({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    client.get(`/projects/${projectId}/cost`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [projectId]);

  if (error) return <p className="text-sm text-rose-700" data-testid="project-cost-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500" data-testid="project-cost-loading">Estimating…</p>;
  const envs = data.environments;
  const sources = [...new Set(Object.values(data.estimates).flatMap((e) => e.sources))];
  const alerts = data.budget_alerts || [];
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="project-cost">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Coins className="h-4 w-4 text-slate-400" /> Estimated monthly cost</h2>
        <span className="font-display text-2xl font-semibold" data-testid="project-cost-total">${data.monthly_usd.toFixed(2)}<span className="ml-1 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">/ mo</span></span>
      </div>
      {alerts.length > 0 && (
        <div className="mb-5 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="project-budget-alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Over budget.</span>{" "}
            {alerts.map((a) => `${a.env}: $${a.monthly_usd.toFixed(2)} vs $${a.budget.toFixed(0)} budget`).join(" · ")}
          </div>
        </div>
      )}
      <div className={cn("grid gap-4", envs.length > 1 ? "md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1")}>
        {envs.map((env) => {
          const e = data.estimates[env];
          const budget = data.budgets?.[env] || 0;
          const over = budget > 0 && e.monthly_usd > budget;
          return (
            <div key={env} className={cn("overflow-hidden rounded-md border", over ? "border-rose-300" : "border-slate-200")} data-testid={`project-cost-env-${env}`}>
              <div className={cn("flex items-center justify-between px-4 py-2.5", over ? "bg-rose-50" : "bg-slate-50")}>
                <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{env}{budget > 0 && <span className="ml-2 text-slate-400">budget ${budget.toFixed(0)}</span>}</span>
                <span className={cn("font-display text-base font-semibold", over && "text-rose-700")} data-testid={`project-cost-env-total-${env}`}>${e.monthly_usd.toFixed(2)}</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {e.items.map((i) => (
                    <tr key={i.key} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-2">
                        <div className="font-mono-tight text-slate-900">{i.key}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{i.basis}</div>
                        <div className="mt-0.5 font-mono-tight text-[10px] text-slate-400">{Object.entries(i.params).map(([k, v]) => `${k}=${v}`).join(" · ")}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right font-medium text-slate-900">${i.monthly_usd.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <p className="mt-4 flex items-start gap-1.5 font-mono-tight text-[10px] text-slate-500">
        <Info className="mt-0.5 h-3 w-3 shrink-0" /> Sources: {sources.join(", ")}. Indicative on-demand list prices with fixed usage assumptions; parameters per environment are taken into account.
      </p>
    </Card>
  );
}
